import { useEffect, useState, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, Firestore, QueryConstraint } from 'firebase/firestore';
import { Booking, BookingStatus } from '../types/booking';
import { getLocalDateStr } from '../utils/dateUtils';

interface UseRealtimeBookingsOptions {
  dateFilter?: string;           // "2026-01-25"
  statusFilter?: BookingStatus[];
  includeCompleted?: boolean;
}

interface UseRealtimeBookingsResult {
  bookings: Booking[];
  loading: boolean;
  error: Error | null;
  isConnected: boolean;
  newBookingAlert: Booking | null;
  clearNewBookingAlert: () => void;
}

export function useRealtimeBookings(
  database: Firestore | null,
  hotelId: string,
  restaurantId: string,
  options: UseRealtimeBookingsOptions = {}
): UseRealtimeBookingsResult {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [newBookingAlert, setNewBookingAlert] = useState<Booking | null>(null);

  const previousIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  const clearNewBookingAlert = useCallback(() => {
    setNewBookingAlert(null);
  }, []);

  useEffect(() => {
    if (!database || !hotelId || !restaurantId) {
      console.log('[useRealtimeBookings] Missing params:', { database: !!database, hotelId, restaurantId });
      setLoading(false);
      return;
    }

    const bookingsPath = `hotels/${hotelId}/bookings`;
    console.log('[useRealtimeBookings] Setting up listener:', { bookingsPath, restaurantId });

    // Build query constraints - using only restaurantId filter to avoid composite index requirement
    // Sorting is done client-side
    const constraints: QueryConstraint[] = [
      where('restaurantId', '==', restaurantId)
    ];

    const q = query(collection(database, bookingsPath), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('[useRealtimeBookings] Snapshot received, docs count:', snapshot.size);

        let data = snapshot.docs.map(doc => {
          const docData = doc.data();
          console.log('[useRealtimeBookings] Doc:', doc.id, 'restaurantId:', docData.restaurantId, 'date:', docData.date);
          // IMPORTANT: Spread docData first, then override id with doc.id
          // This ensures we always use the Firestore document ID, not any stored 'id' field
          // that might be mismatched (e.g., from old AI Concierge bookings)
          return {
            ...docData,
            id: doc.id,
          };
        }) as Booking[];

        // Sort client-side instead of using orderBy to avoid index requirements
        data.sort((a, b) => {
          const dateCompare = a.date.localeCompare(b.date);
          if (dateCompare !== 0) return dateCompare;
          return a.time.localeCompare(b.time);
        });

        // Client-side filtering for date
        if (options.dateFilter) {
          data = data.filter(b => b.date === options.dateFilter);
        }

        // Client-side filtering for status
        if (options.statusFilter?.length) {
          data = data.filter(b => options.statusFilter!.includes(b.status));
        }

        // Exclude completed unless specified
        if (!options.includeCompleted) {
          data = data.filter(b =>
            !['completed', 'cancelled', 'no-show'].includes(b.status)
          );
        }

        // Detect new bookings (after initial load)
        const currentIds = new Set(data.map(b => b.id));
        if (!isInitialLoadRef.current && previousIdsRef.current.size > 0) {
          const newIds = [...currentIds].filter(id => !previousIdsRef.current.has(id));
          if (newIds.length > 0) {
            // Find the newest booking
            const newBooking = data.find(b => b.id === newIds[0]);
            if (newBooking) {
              setNewBookingAlert(newBooking);
              // Play notification sound if available
              try {
                const audio = new Audio('/notification.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => { /* ignore audio errors */ });
              } catch (e) {
                // Audio not available
              }
            }
          }
        }

        previousIdsRef.current = currentIds;
        isInitialLoadRef.current = false;

        setBookings(data);
        setLoading(false);
        setIsConnected(true);
        setError(null);
      },
      (err) => {
        console.error('Booking listener error:', err);
        setError(err);
        setLoading(false);
        setIsConnected(false);
      }
    );

    return () => {
      unsubscribe();
      isInitialLoadRef.current = true;
    };
  }, [
    database,
    hotelId,
    restaurantId,
    options.dateFilter,
    options.statusFilter?.join(','),
    options.includeCompleted
  ]);

  return {
    bookings,
    loading,
    error,
    isConnected,
    newBookingAlert,
    clearNewBookingAlert
  };
}

// Hook for getting all bookings with completed ones
export function useAllBookings(
  database: Firestore | null,
  hotelId: string,
  restaurantId: string,
  dateFilter?: string
) {
  return useRealtimeBookings(database, hotelId, restaurantId, {
    dateFilter,
    includeCompleted: true
  });
}

// Hook for today's active bookings only
export function useTodayBookings(
  database: Firestore | null,
  hotelId: string,
  restaurantId: string
) {
  const today = getLocalDateStr();
  return useRealtimeBookings(database, hotelId, restaurantId, {
    dateFilter: today,
    includeCompleted: false
  });
}
